// Iter57 — Public SEO Landing Page
// Renders /plans/{facet}/{slug} — the indexable, per-facet marketing page.
// Composed of:
//   • Hero with breadcrumbs
//   • AI-approved (or manual) intro
//   • Featured plans grid
//   • Related plans grid
//   • Internal links to sibling landing pages
//   • Schema.org JSON-LD (CollectionPage + ItemList + BreadcrumbList)
import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import PlanCard from "@/components/PlanCard";
import { setMetaTag, setLinkTag } from "@/lib/seo";
import { ChevronRight, Sparkles } from "lucide-react";

const LandingPage = ({ facetType }) => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setNotFound(false);
      try {
        const { data } = await api.get(`/landing/${facetType}/${slug}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) {
          if (e.response?.status === 404) setNotFound(true);
          else setNotFound(true);
        }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [facetType, slug]);

  // Inject SEO tags + JSON-LD once data lands.
  useEffect(() => {
    if (!data) return;
    const seo = data.seo || {};
    document.title = seo.meta_title || data.title;
    const cleanups = [];
    cleanups.push(setMetaTag("name", "description", seo.meta_description || ""));
    cleanups.push(setMetaTag("name", "robots", seo.robots || "index,follow"));
    cleanups.push(setMetaTag("property", "og:title", seo.meta_title || data.title));
    cleanups.push(setMetaTag("property", "og:description", seo.meta_description || ""));
    cleanups.push(setMetaTag("property", "og:type", "website"));
    if (seo.og_image) cleanups.push(setMetaTag("property", "og:image", seo.og_image));
    if (seo.canonical_url) {
      const abs = seo.canonical_url.startsWith("http") ? seo.canonical_url : `${window.location.origin}${seo.canonical_url}`;
      cleanups.push(setLinkTag("canonical", abs));
    }
    // JSON-LD: CollectionPage + BreadcrumbList
    const ldId = "landing-jsonld";
    let script = document.getElementById(ldId);
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = ldId;
      document.head.appendChild(script);
    }
    const jsonLd = [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: data.title,
        description: seo.meta_description || data.intro?.slice(0, 200),
        url: typeof window !== "undefined" ? window.location.href : undefined,
        mainEntity: {
          "@type": "ItemList",
          numberOfItems: (data.featured_plans || []).length + (data.related_plans || []).length,
          itemListElement: [...(data.featured_plans || []), ...(data.related_plans || [])].slice(0, 24).map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${window.location.origin}/plans/${p.slug || p.id}`,
            name: p.title,
          })),
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: (data.breadcrumbs || []).map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.label,
          item: `${window.location.origin}${b.href}`,
        })),
      },
    ];
    script.textContent = JSON.stringify(jsonLd);
    return () => {
      cleanups.forEach((fn) => fn && fn());
      const s = document.getElementById(ldId);
      if (s) s.remove();
    };
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground" data-testid="landing-loading">Loading…</div>
    );
  }
  if (notFound || !data) {
    return <Navigate to="/plans" replace />;
  }

  const banner = data.banner || {};
  const heroImg = banner.image || (data.featured_plans?.[0]?.images?.[0]) || "";

  return (
    <div className="min-h-screen bg-background" data-testid={`landing-${facetType}-${slug}`}>
      {/* Hero */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            {/* Breadcrumbs */}
            <nav className="flex items-center flex-wrap gap-1 text-[11px] uppercase tracking-[0.16em] text-muted-foreground mb-4" data-testid="landing-breadcrumbs">
              {(data.breadcrumbs || []).map((b, i, arr) => (
                <React.Fragment key={i}>
                  {i < arr.length - 1 ? (
                    <Link to={b.href} className="hover:text-foreground">{b.label}</Link>
                  ) : (
                    <span className="text-foreground">{b.label}</span>
                  )}
                  {i < arr.length - 1 && <ChevronRight size={10} className="mx-1 opacity-60" />}
                </React.Fragment>
              ))}
            </nav>
            <div className="overline text-primary mb-2">{facetType.charAt(0).toUpperCase() + facetType.slice(1)}</div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]" data-testid="landing-title">
              {banner.title || data.title}
            </h1>
            {banner.subtitle && <p className="mt-4 text-lg text-muted-foreground max-w-2xl">{banner.subtitle}</p>}
            <div className="mt-6 text-xs text-muted-foreground flex items-center gap-3">
              <span data-testid="landing-total">{data.total_plans} plan{data.total_plans === 1 ? "" : "s"} in this collection</span>
            </div>
          </div>
          {heroImg && (
            <div className="lg:col-span-5">
              <div className="aspect-[4/3] bg-neutral-100 overflow-hidden">
                <img src={heroImg} alt={data.title} className="w-full h-full object-cover" loading="eager" />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* AI Intro (only shown if approved or manually written) */}
      {data.intro && (
        <section className="border-b border-border">
          <div className="max-w-3xl mx-auto px-6 md:px-10 py-14">
            <div className="overline text-primary mb-3 flex items-center gap-2"><Sparkles size={12} /> About this collection</div>
            <div className="prose prose-neutral max-w-none whitespace-pre-wrap leading-relaxed text-base md:text-lg" data-testid="landing-intro">
              {data.intro}
            </div>
          </div>
        </section>
      )}

      {/* Featured plans */}
      {data.featured_plans?.length > 0 && (
        <section className="border-b border-border">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
            <div className="flex items-end justify-between mb-8">
              <div>
                <div className="overline text-primary">Featured</div>
                <h2 className="font-display text-3xl md:text-4xl mt-2">Handpicked for you</h2>
              </div>
              <Link to="/plans" className="text-xs uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground">Browse all →</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="landing-featured-grid">
              {data.featured_plans.map((p) => (<PlanCard key={p.id} plan={p} />))}
            </div>
          </div>
        </section>
      )}

      {/* Related plans */}
      {data.related_plans?.length > 0 && (
        <section className="border-b border-border">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
            <div className="overline text-primary mb-2">More {data.title}</div>
            <h2 className="font-display text-3xl md:text-4xl mb-8">Continue exploring</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="landing-related-grid">
              {data.related_plans.map((p) => (<PlanCard key={p.id} plan={p} />))}
            </div>
          </div>
        </section>
      )}

      {/* Internal links */}
      {data.internal_links?.length > 0 && (
        <section>
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-14">
            <div className="overline text-primary mb-3">More {facetType} pages</div>
            <div className="flex flex-wrap gap-2" data-testid="landing-internal-links">
              {data.internal_links.map((l) => (
                <Link key={l.href} to={l.href} className="px-4 py-2 border border-border hover:border-primary text-sm transition-colors">
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default LandingPage;
